import { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { EditAsUrlSync } from './contexts/hooks/EditAsUrlSync';
import { useMetadata } from './contexts/MetadataContext';
import { useMegaMenu } from './contexts/MegaMenuContext';
import Overview from './pages/overview';
import CreateWrapper from './pages/create/CreateWrapper';
import AuthSuccess from './pages/AuthSuccess';
import { Footer } from './components/common/Footer';
import Admin from './pages/Admin';
import { useBrand } from './contexts/BrandingContext';
import AdminBranding from './components/admin/branding/AdminBranding';
import TemplateEditor from './components/admin/template/TemplateEditor';
import MediaSyncPage from './pages/media-sync';
import { Header } from './components/common/Header';
import { useFeatureFlags } from './contexts/FeatureFlagsContext';
import ListingManagementGate from './components/common/ListingManagementGate';
import ListingAccessPendingGate from './components/common/ListingAccessPendingGate';
import NoAccessGate from './components/common/NoAccessGate';
import CreateListingGate from './components/common/CreateListingGate';
import EditRoutes from './pages/edit/EditRoutes';
import { FeatureFlags } from './flags';
import ProtectedRoute from './pages/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import { useAnalytics } from './contexts/AnalyticsContext';
import { useTenant } from './contexts/TenantContext';
import { useTenantPath } from './hooks/useTenantPath';
import Loader from './components/common/loader';
import { useUserTermsCheck } from './hooks/useUserTermsCheck';
import { useBrightChromeObserver } from './hooks/useBrightChromeObserver';

export function AppContent() {
  const { isLoading: tenantLoading, error: tenantError } = useTenant();
  const { session, isAuthenticated } = useAuth();
  const {
    loadMetadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useMetadata();
  const {
    loadBranding,
    isLoading: brandingLoading,
    error: brandingError,
  } = useBrand();
  const { isMenuInitialized, initializeMegaMenu } = useMegaMenu();
  const {
    initializeFlags,
    isLoading: flagsLoading,
    hasFeature,
  } = useFeatureFlags();
  const { initializeAnalytics, isLoading: analyticsLoading } = useAnalytics();
  const [isAppReady, setIsAppReady] = useState(false);
  const { getPath, getTenantRoute, getTenantRootRoute } = useTenantPath();

  // Publish --bright-header-height / --bright-footer-height CSS vars
  useBrightChromeObserver();

  // Step 3: Check user terms acceptance (gates all entry to NGW)
  const termsAccepted = useUserTermsCheck();
  // Step 1: When authentication is complete, load resources
  useEffect(() => {
    // Only initialize resources if we're authenticated
    if (session && isAuthenticated && !tenantLoading) {
      Promise.all([
        loadMetadata(session),
        loadBranding(session),
        initializeFlags(session),
        initializeAnalytics(session),
      ])
        .then(() => {
          // Only set app ready after all resources are loaded
          setIsAppReady(true);
        })
        .catch((error) => {
          console.error('Error loading app resources:', error);
          // Still set app ready so we can show error state
          setIsAppReady(true);
        });
    }
  }, [session, isAuthenticated, tenantLoading]);

  // Step 2: Initialize mega menu after resources are loaded
  useEffect(() => {
    if (
      !isMenuInitialized &&
      isAppReady &&
      !metadataLoading &&
      !brandingLoading &&
      !flagsLoading &&
      !analyticsLoading &&
      isAuthenticated
    ) {
      initializeMegaMenu();
    }
  }, [
    isMenuInitialized,
    isAppReady,
    metadataLoading,
    brandingLoading,
    flagsLoading,
    analyticsLoading,
    initializeMegaMenu,
    isAuthenticated,
  ]);

  // Show error if there's a problem loading tenant
  if (tenantError) {
    return (
      <>
        <Header isMegaMenuLoaded={isMenuInitialized} />
        <div>
          <h1>Error</h1>
          <p>Failed to initialize tenant: {tenantError.message}</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
        <Footer />
      </>
    );
  }

  // Show error if there's a problem loading resources
  if (isAppReady && (metadataError || brandingError)) {
    return (
      <>
        <Header isMegaMenuLoaded={isMenuInitialized} />
        <div>
          <h1>Error</h1>
          <p>{metadataError?.message || brandingError?.message}</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
        <Footer />
      </>
    );
  }

  // Show loading indicator if resources are still loading
  // But we still render Header and Footer to avoid flashing
  if (
    !isAppReady ||
    metadataLoading ||
    brandingLoading ||
    flagsLoading ||
    tenantLoading ||
    analyticsLoading
  ) {
    return (
      <Loader
        isMenuInitialized={isMenuInitialized}
        text={'Loading application resources...'}
      />
    );
  }

  // Wait for terms acceptance check before rendering routes
  // This gates any entry to NGW (hijacks first render)
  if (termsAccepted === null) {
    return (
      <Loader
        isMenuInitialized={isMenuInitialized}
        text={'Checking user terms...'}
      />
    );
  }

  // App is fully loaded and ready - render routes
  // Use NGW overview as default if flag is enabled, otherwise use create
  const showOverview = hasFeature(FeatureFlags.NGW_OVERVIEW);
  const defaultRoute = showOverview ? getPath('/overview') : getPath('/create');

  return (
    <>
      <Header isMegaMenuLoaded={isMenuInitialized} />
      <ListingAccessPendingGate>
        <NoAccessGate>
          <ListingManagementGate>
            <Router>
              <EditAsUrlSync />
              <Routes>
                {/* Tenant root route - must come before other routes */}
                {getTenantRootRoute()}

                {/* Now directly use Route elements returned by the hook */}
                {getTenantRoute('/auth-success', <AuthSuccess />)}
                {getTenantRoute('/overview', <Overview />)}
                {getTenantRoute('/edit/:listingKey/*', <EditRoutes />)}
                {getTenantRoute('/media-sync', <MediaSyncPage />)}
                {getTenantRoute(
                  '/create',
                  <CreateListingGate>
                    <CreateWrapper />
                  </CreateListingGate>,
                )}

                {/* Protected Admin Routes */}
                {getTenantRoute(
                  '/admin',
                  <ProtectedRoute
                    element={<Admin />}
                    featureFlag={FeatureFlags.NGW_ADMIN}
                  />,
                )}
                {getTenantRoute(
                  '/admin/template',
                  <ProtectedRoute
                    element={<TemplateEditor />}
                    featureFlag={FeatureFlags.NGW_ADMIN}
                  />,
                )}
                {getTenantRoute(
                  '/admin/branding',
                  <ProtectedRoute
                    element={<AdminBranding />}
                    featureFlag={FeatureFlags.NGW_ADMIN}
                  />,
                )}

                {/* Analytics Dashboard */}
                {getTenantRoute(
                  '/dashboard',
                  <ProtectedRoute
                    element={<Dashboard />}
                    featureFlag={FeatureFlags.NGW_ANALYTICS}
                  />,
                )}

                {/* Root path handler */}
                {getTenantRoute('/', <Navigate replace to={defaultRoute} />)}

                {/* Catch-all route for 404s */}
                <Route path="*" element={<div>Page not found</div>} />
              </Routes>
            </Router>
          </ListingManagementGate>
        </NoAccessGate>
      </ListingAccessPendingGate>
      <Footer />
    </>
  );
}
